/**
 * The reflection calendar's day map (issue #1222, round-11
 * `ReflectionCalendarIcons`). Pure: four reads in, one Map out, so every
 * placement rule is testable without a render.
 *
 * Placement (D240, D231):
 *   - a match sits on `match_date`; `planned` only when `status` says so.
 *     A planned match is never "done", whatever its date (D231).
 *   - a training session sits on `training_date`; `planned` when `status`
 *     is "planned".
 *   - a goal sits on the day it was marked done (`achieved_at`, read on the
 *     player's local day) when it is completed and that stamp is set;
 *     otherwise on `target_date`; with neither it is not shown. An open goal
 *     is always planned (dashed), even past its due date (D290).
 *   - `written` is any reflection-log entry on the day (the round-10 dot).
 *   A day is "done" if it has a done item or writing, "planned" if it has
 *   only planned items.
 */
import { todayIso } from "./trainingStatus";

/**
 * One table for the legend and the grid: the board's own glyph paths (24px
 * viewBox, stroke only), the word on the legend, and the words a selected
 * day's row uses. Colour never carries the kind, the shape does.
 */
export const CALENDAR_KINDS = {
  match: {
    label: "Match",
    plannedLabel: "Planned match",
    paths: ["M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z", "M8.5 4c2 5 2 11 0 16M15.5 4c-2 5-2 11 0 16"],
  },
  training: {
    label: "Training",
    plannedLabel: "Planned training",
    paths: ["M6 6v12M18 6v12M3 9v6M21 9v6M6 12h12"],
  },
  goal: {
    label: "Goal",
    doneLabel: "Goal reached",
    plannedLabel: "Goal due",
    paths: ["M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z", "M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z"],
  },
};

/** Render order inside a day, and in the legend. */
export const CALENDAR_KIND_ORDER = ["match", "training", "goal"];

const DAY_RE = /^\d{4}-\d{2}-\d{2}/;

/** `YYYY-MM-DD` from a date or timestamp string, or null. */
function dayOf(value) {
  return typeof value === "string" && DAY_RE.test(value) ? value.slice(0, 10) : null;
}

/** The player's LOCAL day for a stored timestamp; a bare date is unchanged. */
function localDayOfStamp(value) {
  if (typeof value !== "string" || !value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? dayOf(value) : todayIso(d);
}

function emptyDay(date, today) {
  return { date, matches: [], training: [], goals: [], written: [], listed: [], isToday: date === today, state: null };
}

export function buildCalendarDays({ entries = [], matches = [], sessions = [], goals = [], today = todayIso() } = {}) {
  const days = new Map();
  const matchIds = new Set(matches.map((m) => m?.match_id).filter(Boolean));
  const at = (date) => {
    if (!days.has(date)) days.set(date, emptyDay(date, today));
    return days.get(date);
  };

  for (const e of entries) {
    const date = dayOf(e?.date);
    if (!date) continue;
    const day = at(date);
    day.written.push(e);
    // D291: a match reflection whose match is in the list rides on that
    // match's row instead of being listed a second time.
    if (!(e.kind === "match_reflection" && e.match_id && matchIds.has(e.match_id))) day.listed.push(e);
  }

  for (const m of matches) {
    const date = dayOf(m?.match_date);
    if (!date) continue;
    const planned = m.status === "planned";
    at(date).matches.push({
      id: m.match_id, kind: "match", date, planned,
      reflection: entries.find((e) => e?.kind === "match_reflection" && e.match_id === m.match_id) || null,
      title: m.team ? `${m.team} v ${m.opponent || "opposition"}` : m.opponent ? `v ${m.opponent}` : "Match",
    });
  }

  for (const s of sessions) {
    const date = dayOf(s?.training_date);
    if (!date) continue;
    at(date).training.push({
      id: s.session_id, kind: "training", date, planned: s.status === "planned",
      title: s.session_title || "Training session",
    });
  }

  for (const g of goals) {
    const completed = g?.status === "completed";
    const achieved = completed ? localDayOfStamp(g.achieved_at) : null;
    const due = dayOf(g?.target_date);
    const date = achieved || due;
    if (!date) continue;
    at(date).goals.push({
      id: g.goal_id, kind: "goal", date,
      // D290: only a reached goal is "done". An open goal is planned (dashed)
      // on its due date, overdue or not.
      planned: !completed,
      reached: completed,
      onAchievedDay: Boolean(achieved),
      title: g.goal_title || g.target || "Goal",
    });
  }

  for (const info of days.values()) {
    const items = [...info.matches, ...info.training, ...info.goals];
    const anyDone = info.written.length > 0 || items.some((i) => !i.planned);
    info.state = anyDone ? "done" : items.length > 0 ? "planned" : null;
  }
  return days;
}

/** Does the day have anything to open? */
export const dayHasContent = (info) => Boolean(info && info.state);

/** Which kinds appear on a day, in legend order, with whether all are planned. */
export function dayKinds(info) {
  if (!info) return [];
  return CALENDAR_KIND_ORDER
    .map((kind) => {
      const list = kind === "match" ? info.matches : kind === "training" ? info.training : info.goals;
      return list.length ? { kind, count: list.length, planned: list.every((i) => i.planned) } : null;
    })
    .filter(Boolean);
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "3 May" for `YYYY-MM-DD`. */
export function shortDate(date) {
  return `${Number(date.slice(8, 10))} ${MONTHS[Number(date.slice(5, 7)) - 1].slice(0, 3)}`;
}

function phrase(kind, count, planned, reached) {
  const many = count > 1;
  if (kind === "match") return planned ? (many ? `${count} planned matches` : "a planned match") : many ? `${count} matches` : "a match";
  if (kind === "training") return planned ? (many ? `${count} planned training sessions` : "a planned training session") : many ? `${count} training sessions` : "a training session";
  if (planned) return many ? `${count} goals due` : "a goal due";
  return reached ? (many ? `${count} goals reached` : "a goal reached") : many ? `${count} goals` : "a goal";
}

/** "3 May: a match, a goal reached, something written" — what is on the day, in words. */
export function dayAriaLabel(date, info, today) {
  const head = date === today ? `${shortDate(date)} (today)` : shortDate(date);
  if (!dayHasContent(info)) return `${head}: nothing`;
  const parts = [];
  for (const { kind } of dayKinds(info)) {
    const list = kind === "match" ? info.matches : kind === "training" ? info.training : info.goals;
    // Split a mixed day (one played, one planned) into two phrases.
    const done = list.filter((i) => !i.planned);
    const plan = list.filter((i) => i.planned);
    if (done.length) parts.push(phrase(kind, done.length, false, done.every((i) => i.reached)));
    if (plan.length) parts.push(phrase(kind, plan.length, true, false));
  }
  if (info.written.length) parts.push("something written");
  return `${head}: ${parts.join(", ")}`;
}
